import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button, Card, CardContent, Badge, Progress } from "@learn-me/ui";
import {
  useCourse,
  useEnrollCourse,
} from "@/features/courses/hooks/useCourses";
import { useAuthStore } from "@/features/auth/store/authStore";
import {
  formatCurrency,
  formatDuration,
  COURSE_LEVEL_LABELS,
} from "@learn-me/utils";
import {
  PlayCircle,
  Clock,
  BookOpen,
  CheckCircle,
  Lock,
  FileText,
  Video,
  Heart,
} from "lucide-react";
import { motion } from "framer-motion";
import { FadeIn, StaggeredList } from "@/components/animated";
import { Breadcrumb } from "@/components/Breadcrumb";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useReducedMotion } from "@/hooks";
import { useWishlistIds, useToggleWishlist } from "@/features/wishlist";
import CertificationDisclaimer from "@/components/legal/CertificationDisclaimer";
import { normalizeImageUrl } from "@/lib/imageUrl";

export default function CourseDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { data: course, isLoading, error } = useCourse(slug!);
  const enrollMutation = useEnrollCourse();
  const prefersReducedMotion = useReducedMotion();
  const [showEnrollConfirm, setShowEnrollConfirm] = useState(false);

  // Wishlist
  const { data: wishlistIds } = useWishlistIds();
  const { toggle: toggleWishlist, isPending: wishlistPending } =
    useToggleWishlist();
  const isInWishlist =
    course?.id && wishlistIds ? wishlistIds.includes(course.id) : false;

  if (isLoading) {
    return (
      <div className="py-8">
        <div className="container">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/2" />
            <div className="h-4 bg-muted rounded w-1/3" />
            <div className="h-64 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="py-16 text-center">
        <div className="container">
          <h1 className="text-2xl font-bold mb-2">Curso no encontrado</h1>
          <p className="text-muted-foreground mb-4">
            El curso que buscas no existe o ha sido eliminado.
          </p>
          <Link to="/courses">
            <Button>Ver todos los cursos</Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleEnrollClick = () => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: `/courses/${slug}` } });
      return;
    }

    if (course.isFree) {
      // Show confirmation dialog for free courses
      setShowEnrollConfirm(true);
    } else {
      navigate(`/checkout/${course.id}`);
    }
  };

  const handleConfirmEnroll = () => {
    enrollMutation.mutate(course.id, {
      onSuccess: () => {
        setShowEnrollConfirm(false);
        navigate(`/learn/${course.slug}`);
      },
    });
  };

  const handleContinueLearning = () => {
    // Find the first incomplete chapter or the first chapter
    const firstIncomplete = course.chapters?.find(
      (ch) => !ch.userProgress?.isCompleted,
    );
    const targetChapter = firstIncomplete || course.chapters?.[0];

    if (targetChapter) {
      navigate(`/learn/${course.slug}/${targetChapter.id}`);
    } else {
      navigate(`/learn/${course.slug}`);
    }
  };

  const totalDuration =
    course.chapters?.reduce((acc, ch) => acc + (ch.videoDuration || 0), 0) || 0;

  // Build breadcrumb items
  const breadcrumbItems = [
    { label: "Cursos", href: "/courses" },
    { label: course.title },
  ];

  return (
    <div className="py-8">
      <div className="container">
        {/* Breadcrumb */}
        <Breadcrumb items={breadcrumbItems} className="mb-6" />

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <FadeIn direction="up">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  {course.level && (
                    <Badge variant="outline">
                      {COURSE_LEVEL_LABELS[course.level] ?? course.level}
                    </Badge>
                  )}
                  {course.isFree && <Badge variant="success">Gratis</Badge>}
                </div>
                <h1 className="text-3xl lg:text-4xl font-bold mb-4">
                  {course.title}
                </h1>
                <p className="text-lg text-muted-foreground">
                  {course.description}
                </p>
              </div>
            </FadeIn>

            {/* Thumbnail */}
            {course.thumbnailUrl && (
              <FadeIn direction="up" delay={0.1}>
                <motion.div
                  className="aspect-video rounded-lg overflow-hidden bg-muted"
                  whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={normalizeImageUrl(course.thumbnailUrl) ?? ""}
                    alt={course.title}
                    className="object-cover w-full h-full"
                  />
                </motion.div>
              </FadeIn>
            )}

            {/* Course Info */}
            <FadeIn direction="up" delay={0.15}>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-muted-foreground" />
                  <span>{course.chapters?.length || 0} capitulos</span>
                </div>
                {totalDuration > 0 && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <span>{formatDuration(totalDuration)}</span>
                  </div>
                )}
              </div>
            </FadeIn>

            {/* Chapters */}
            <FadeIn direction="up" delay={0.2}>
              <div>
                <h2 className="text-2xl font-bold mb-4">Contenido del Curso</h2>
                <StaggeredList className="space-y-2" staggerDelay={0.05}>
                  {course.chapters?.map((chapter, index) => (
                    <motion.div
                      key={chapter.id}
                      whileHover={prefersReducedMotion ? {} : { x: 4 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Card className="overflow-hidden">
                        <CardContent className="p-4 flex items-center gap-4">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                            {chapter.userProgress?.isCompleted ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : chapter.isLocked ? (
                              <Lock className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <span className="text-sm font-medium">
                                {index + 1}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium truncate">
                              {chapter.title}
                            </h3>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              {chapter.videoDuration && (
                                <span className="flex items-center gap-1">
                                  <Video className="h-3 w-3" />
                                  {formatDuration(chapter.videoDuration)}
                                </span>
                              )}
                              {chapter.isFree && (
                                <Badge variant="outline" className="text-xs">
                                  Vista previa
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </StaggeredList>
              </div>
            </FadeIn>
          </div>

          {/* Sidebar */}
          <FadeIn direction="left" delay={0.25}>
            <div className="lg:col-span-1">
              <Card className="sticky top-24">
                <CardContent className="p-6 space-y-6">
                  {/* Price */}
                  <div className="text-center">
                    {!course.isFree &&
                    course.hasDiscount &&
                    course.listPrice != null &&
                    Number(course.listPrice) > Number(course.salePrice ?? 0) ? (
                      <div className="space-y-1">
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-lg text-muted-foreground line-through">
                            {formatCurrency(
                              Number(course.listPrice),
                              course.currency,
                            )}
                          </span>
                          <Badge variant="success">
                            {Math.round(
                              ((Number(course.listPrice) -
                                Number(course.salePrice ?? 0)) /
                                Number(course.listPrice)) *
                                100,
                            )}
                            % OFF
                          </Badge>
                        </div>
                        <div className="text-3xl font-bold text-primary-600">
                          {formatCurrency(
                            course.salePrice ?? 0,
                            course.currency,
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-3xl font-bold">
                        {course.isFree
                          ? "Gratis"
                          : formatCurrency(
                              course.salePrice ?? 0,
                              course.currency,
                            )}
                      </div>
                    )}
                  </div>

                  {/* Progress or CTA */}
                  {course.isEnrolled ? (
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span>Progreso</span>
                          <span>{course.userProgress || 0}%</span>
                        </div>
                        <Progress value={course.userProgress || 0} />
                      </div>
                      <Button
                        className="w-full"
                        size="lg"
                        onClick={handleContinueLearning}
                      >
                        <PlayCircle className="mr-2 h-5 w-5" />
                        Continuar aprendiendo
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Button
                        className="w-full"
                        size="lg"
                        onClick={handleEnrollClick}
                        isLoading={enrollMutation.isPending}
                      >
                        {course.isFree ? "Inscribirse Gratis" : "Comprar Curso"}
                      </Button>
                      {isAuthenticated && (
                        <Button
                          variant="outline"
                          className="w-full"
                          size="lg"
                          onClick={() =>
                            toggleWishlist(course.id, isInWishlist)
                          }
                          disabled={wishlistPending}
                        >
                          <Heart
                            className={`mr-2 h-5 w-5 ${isInWishlist ? "fill-red-500 text-red-500" : ""}`}
                          />
                          {isInWishlist
                            ? "En favoritos"
                            : "Agregar a favoritos"}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Course includes */}
                  <div className="border-t pt-6 space-y-3">
                    <h4 className="font-semibold">Este curso incluye:</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <Video className="h-4 w-4 text-muted-foreground" />
                        Videos de alta calidad
                      </li>
                      <li className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        Material descargable
                      </li>
                      <li className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-muted-foreground" />
                        Quizzes interactivos
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-muted-foreground" />
                        Acceso de por vida
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </FadeIn>
        </div>

        <div className="mt-8">
          <CertificationDisclaimer />
        </div>
      </div>

      {/* Enrollment Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showEnrollConfirm}
        onClose={() => setShowEnrollConfirm(false)}
        onConfirm={handleConfirmEnroll}
        title={`Inscribirse en "${course.title}"`}
        description={`Este curso incluye ${course.chapters?.length || 0} capítulos${totalDuration > 0 ? ` y aproximadamente ${formatDuration(totalDuration)} de contenido` : ""}. ¿Deseas inscribirte?`}
        confirmText="Sí, inscribirme"
        cancelText="Cancelar"
        variant="info"
        isLoading={enrollMutation.isPending}
      />
    </div>
  );
}
